import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router } from '@inertiajs/react'
import { AppShell } from '@/components/layout/AppShell'
import { PageHeader } from '@/components/landing/PageHeader'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const schema = z.object({
  name: z.string().min(1, 'Organization name is required').max(255),
})

type FormValues = z.infer<typeof schema>

interface Props {
  user: { email: string; role: string }
  organization: { name: string; slug: string }
  errors: Record<string, string[]>
}

export default function SettingsIndex({ user, organization, errors }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors: fieldErrors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: organization.name },
  })

  function onSubmit(data: FormValues) {
    router.patch('/dashboard/settings', { organization: data })
  }

  return (
    <AppShell user={user} organization={organization}>
      <div className="max-w-2xl space-y-8">
        <PageHeader
          eyebrow="Settings"
          title="Settings"
          description="Manage your organization settings."
        />

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Account
          </h2>
          <dl className="mt-4 divide-y divide-foreground/[0.06] text-sm">
            <div className="flex items-center justify-between py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Email
              </dt>
              <dd>{user.email}</dd>
            </div>
            <div className="flex items-center justify-between py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Role
              </dt>
              <dd className="capitalize">{user.role}</dd>
            </div>
          </dl>
        </section>

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight">
            Organization
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Organization name
              </Label>
              <Input id="name" {...register('name')} className="rounded-md" />
              {(fieldErrors.name?.message || errors['name']?.[0]) && (
                <p className="text-xs text-destructive">
                  {fieldErrors.name?.message ?? errors['name']?.[0]}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Slug
              </Label>
              <Input value={organization.slug} readOnly className="rounded-md text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                Slug is auto-generated and cannot be changed.
              </p>
            </div>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
          </form>
        </section>

        <section className="surface-card p-6">
          <h2 className="font-heading text-base font-semibold tracking-tight text-destructive">
            Danger zone
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Organization deletion and team management are available in a future update.
          </p>
        </section>
      </div>
    </AppShell>
  )
}
